import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Compass,
  Sparkles,
  Shield,
  Home,
  Flame,
  Droplets,
  Wind,
  Sun,
  Moon,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Info,
  Lock,
  BookOpen,
  Heart,
  Award,
  Layers,
  Search,
  Filter,
  Check,
  XCircle,
  HelpCircle
} from 'lucide-react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  VASTU_16_ZONES,
  SIGNIFICANT_PLACEMENTS,
  BRAHMASTHAN_DATA,
  calculateAstroVastuCorrelations,
  evaluateVastuPlacement,
  VastuZone,
  SignificantPlacement
} from '../utils/vastuEngine';

interface VastuViewProps {
  kundali?: CompleteKundali | null;
  lang?: Language;
}

export const VastuView: React.FC<VastuViewProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';

  // Sub-tabs
  const [activeSection, setActiveSection] = useState<
    'compass' | 'significant' | 'checker' | 'astrovastu' | 'remedies'
  >('compass');

  // Selected Zone for Compass & Details
  const [selectedZoneId, setSelectedZoneId] = useState<string>('NE');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedPlacementId, setSelectedPlacementId] = useState<string>('pooja_room');

  // Interactive Room Placement Evaluator State
  const [checkerRoom, setCheckerRoom] = useState<string>('pooja_room');
  const [checkerZone, setCheckerZone] = useState<string>('NE');

  const selectedZone = VASTU_16_ZONES.find((z) => z.id === selectedZoneId) || VASTU_16_ZONES[2];
  const selectedPlacement =
    SIGNIFICANT_PLACEMENTS.find((p) => p.id === selectedPlacementId) || SIGNIFICANT_PLACEMENTS[0];

  const astroVastuData = calculateAstroVastuCorrelations(kundali);

  // Evaluate user selected room + zone via deterministic canonical engine
  const evalResult = evaluateVastuPlacement(checkerRoom, checkerZone);

  return (
    <div className="space-y-6 pb-16">
      {/* Hero Title & Vedic Vastu Summary Header */}
      <div className="bg-gradient-to-r from-astro-primary via-[#1F5F5E] to-astro-primary rounded-2xl p-6 sm:p-8 text-astro-bg shadow-sm relative overflow-hidden border border-astro-border">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm border border-white/10 mb-3">
              <Compass className="w-3.5 h-3.5 text-amber-300" />
              <span>{isHi ? 'वैदिक वास्तु शास्त्र एवं दिशा विज्ञान' : 'Vedic Vastu Shastra & Directional Harmony'}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight text-white">
              {isHi ? 'वास्तु मंडल, 16 दिशाएं एवं गृह स्थान' : 'Vastu Purusha Mandala & 16-Zone House Guide'}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-teal-100 max-w-2xl leading-relaxed">
              {isHi
                ? 'पूजा घर (ईशान कोण), तिजोरी व कुबेर स्थान (उत्तर/नैऋत्य), रसोई (आग्नेय) एवं शयनकक्ष की प्रामाणिक वैदिक दिशाएं, वास्तु चक्र एवं बिना तोड़-फोड़ के अचूक उपाय।'
                : 'Sacred directional placements for Pooja Room (NE), Tijori & Wealth Safe (North/SW), Kitchen (SE), Master Bedroom (SW), 16 energy zones, and non-destructive Vedic remedies.'}
            </p>
          </div>

          {/* Quick Stats Badges */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center border border-white/10">
              <span className="block text-2xl font-bold text-amber-300 font-serif">16</span>
              <span className="text-[11px] text-teal-100 uppercase tracking-wider">
                {isHi ? 'वास्तु दिशाएं' : 'Vastu Zones'}
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center border border-white/10">
              <span className="block text-2xl font-bold text-amber-300 font-serif">5</span>
              <span className="text-[11px] text-teal-100 uppercase tracking-wider">
                {isHi ? 'पंच महाभूत' : 'Elements'}
              </span>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 text-center border border-white/10">
              <span className="block text-2xl font-bold text-amber-300 font-serif">100%</span>
              <span className="text-[11px] text-teal-100 uppercase tracking-wider">
                {isHi ? 'वैदिक उपाय' : 'Remedies'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 bg-astro-card rounded-xl border border-astro-border">
        {[
          {
            id: 'compass',
            label: isHi ? '16 दिशा चक्र (वास्तु मंडल)' : '16-Zone Compass Wheel',
            icon: Compass
          },
          {
            id: 'significant',
            label: isHi ? 'महत्वपूर्ण स्थान (पूजा घर, तिजोरी, आदि)' : 'Pooja Room, Tijori & Spaces',
            icon: Sparkles
          },
          {
            id: 'checker',
            label: isHi ? 'दिशा मूल्यांकन (Quick Checker)' : 'Room Direction Evaluator',
            icon: CheckCircle2
          },
          {
            id: 'astrovastu',
            label: isHi ? 'कुंडली-वास्तु समन्वय (Astro-Vastu)' : 'Kundali Astro-Vastu Link',
            icon: Sun
          },
          {
            id: 'remedies',
            label: isHi ? 'वैदिक निवारण उपाय (बिना तोड़-फोड़)' : 'Non-Destructive Remedies',
            icon: Shield
          }
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeSection === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-astro-primary text-astro-bg shadow-sm'
                  : 'text-[#4A5553] hover:text-astro-primary hover:bg-white/60'
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-amber-300' : 'text-astro-secondary-text'}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* SECTION 1: 16-ZONE VASTU PURUSHA MANDALA & INTERACTIVE COMPASS */}
      {activeSection === 'compass' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: 16-Zone Interactive Compass Visualizer */}
            <div className="lg:col-span-7 bg-astro-bg rounded-2xl p-6 border border-astro-border shadow-sm flex flex-col items-center justify-between">
              <div className="w-full flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-astro-text font-serif flex items-center gap-2">
                    <Compass className="w-5 h-5 text-astro-primary" />
                    {isHi ? '16 दिशा वास्तु चक्र' : 'Interactive 16-Zone Vastu Mandala'}
                  </h3>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? 'किसी भी दिशा पर क्लिक करें और उसकी ऊर्जा, स्वामी व अनुकूलता देखें'
                      : 'Click any quadrant to inspect its element, ruling deity, and suitable items'}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">
                    {isHi ? '360° महावास्तु' : '360° MahaVastu'}
                  </span>
                </div>
              </div>

              {/* Interactive SVG Compass Dial */}
              <div className="relative w-full max-w-[420px] aspect-square my-2 flex items-center justify-center">
                <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-sm select-none">
                  {/* Outer Ring */}
                  <circle cx="200" cy="200" r="190" fill="#FAF7F0" stroke="#D1C7B7" strokeWidth="2" />
                  <circle cx="200" cy="200" r="182" fill="none" stroke="#E5DDD0" strokeWidth="1" strokeDasharray="3,3" />

                  {/* 16 Radial Wedges */}
                  {VASTU_16_ZONES.map((zone, idx) => {
                    const isSelected = zone.id === selectedZoneId;
                    const angleStep = 360 / 16;
                    const startAngle = (idx * angleStep - angleStep / 2 - 90) * (Math.PI / 180);
                    const endAngle = (idx * angleStep + angleStep / 2 - 90) * (Math.PI / 180);

                    const rOuter = 180;
                    const rInner = 60;

                    const x1 = 200 + rOuter * Math.cos(startAngle);
                    const y1 = 200 + rOuter * Math.sin(startAngle);
                    const x2 = 200 + rOuter * Math.cos(endAngle);
                    const y2 = 200 + rOuter * Math.sin(endAngle);

                    const x3 = 200 + rInner * Math.cos(endAngle);
                    const y3 = 200 + rInner * Math.sin(endAngle);
                    const x4 = 200 + rInner * Math.cos(startAngle);
                    const y4 = 200 + rInner * Math.sin(startAngle);

                    const midAngle = (idx * angleStep - 90) * (Math.PI / 180);
                    const labelR = 125;
                    const lx = 200 + labelR * Math.cos(midAngle);
                    const ly = 200 + labelR * Math.sin(midAngle);

                    // Element-based fills
                    let fillColor = '#F1F5F9';
                    if (zone.element === 'Water') fillColor = isSelected ? '#38BDF8' : '#E0F2FE';
                    else if (zone.element === 'Air') fillColor = isSelected ? '#4ADE80' : '#DCFCE7';
                    else if (zone.element === 'Fire') fillColor = isSelected ? '#FB7185' : '#FEE2E2';
                    else if (zone.element === 'Earth') fillColor = isSelected ? '#FACC15' : '#FEF08A';
                    else if (zone.element === 'Space') fillColor = isSelected ? '#94A3B8' : '#E2E8F0';

                    return (
                      <g
                        key={zone.id}
                        onClick={() => setSelectedZoneId(zone.id)}
                        className="cursor-pointer transition-all duration-200 group"
                      >
                        <path
                          d={`M ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 0 0 ${x4} ${y4} Z`}
                          fill={fillColor}
                          stroke={isSelected ? '#174C4B' : '#CBD5E1'}
                          strokeWidth={isSelected ? '2.5' : '1'}
                          className="hover:opacity-90"
                        />
                        <text
                          x={lx}
                          y={ly}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={isSelected ? '12' : '10'}
                          fontWeight={isSelected ? 'bold' : '600'}
                          fill={isSelected ? '#0F172A' : '#334155'}
                          className="pointer-events-none font-sans"
                        >
                          {zone.id}
                        </text>
                      </g>
                    );
                  })}

                  {/* Brahmasthan Center Circle */}
                  <g
                    onClick={() => setSelectedZoneId('BRAHMA')}
                    className="cursor-pointer group"
                  >
                    <circle
                      cx="200"
                      cy="200"
                      r="54"
                      fill="#FFFDF7"
                      stroke="#174C4B"
                      strokeWidth="2.5"
                      className="group-hover:fill-amber-50"
                    />
                    <circle cx="200" cy="200" r="46" fill="none" stroke="#D1C7B7" strokeDasharray="2,2" />
                    <text
                      x="200"
                      y="194"
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="bold"
                      fill="#B58A4A"
                      className="font-serif"
                    >
                      ॐ
                    </text>
                    <text
                      x="200"
                      y="210"
                      textAnchor="middle"
                      fontSize="9"
                      fontWeight="600"
                      fill="#4A5553"
                    >
                      Brahmasthan
                    </text>
                  </g>
                </svg>
              </div>

              {/* Elements Color Legend */}
              <div className="w-full grid grid-cols-5 gap-2 pt-4 border-t border-astro-border text-center text-[11px] font-medium text-[#4A5553]">
                <div className="flex items-center justify-center gap-1.5 p-1.5 rounded-lg bg-sky-50 border border-sky-200">
                  <div className="w-2.5 h-2.5 rounded-full bg-sky-500" />
                  <span>{isHi ? 'जल (Water)' : 'Water (N/NE)'}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 p-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span>{isHi ? 'वायु (Air)' : 'Air (E/ENE)'}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 p-1.5 rounded-lg bg-rose-50 border border-rose-200">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                  <span>{isHi ? 'अग्नि (Fire)' : 'Fire (SE/S)'}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 p-1.5 rounded-lg bg-astro-secondary-surface border border-[#E2DED5]">
                  <div className="w-2.5 h-2.5 rounded-full bg-astro-secondary-surface0" />
                  <span>{isHi ? 'पृथ्वी (Earth)' : 'Earth (SW/SSW)'}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 p-1.5 rounded-lg bg-astro-secondary-surface border border-astro-border">
                  <div className="w-2.5 h-2.5 rounded-full bg-astro-secondary" />
                  <span>{isHi ? 'आकाश (Space)' : 'Space (W/WSW)'}</span>
                </div>
              </div>
            </div>

            {/* Right: Selected Zone Detailed Intelligence Card */}
            <div className="lg:col-span-5 flex flex-col space-y-4">
              {selectedZoneId === 'BRAHMA' ? (
                /* Brahmasthan Special Panel */
                <div className="bg-astro-bg rounded-2xl p-6 border border-astro-border shadow-sm space-y-4 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between pb-3 border-b border-astro-border">
                      <div>
                        <span className="text-xs uppercase font-bold text-[#52635F] tracking-wider">
                          Cosmic Nucleus of the House
                        </span>
                        <h4 className="text-xl font-bold text-astro-primary font-serif">
                          {isHi ? BRAHMASTHAN_DATA.nameHi : BRAHMASTHAN_DATA.name}
                        </h4>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-astro-primary text-astro-accent flex items-center justify-center font-bold text-lg shadow-sm border border-astro-primary">
                        ॐ
                      </div>
                    </div>

                    <div className="mt-4 p-3 rounded-xl bg-astro-card border border-astro-border text-xs text-astro-text leading-relaxed">
                      {isHi ? BRAHMASTHAN_DATA.significanceHi : BRAHMASTHAN_DATA.significance}
                    </div>

                    <div className="mt-4 space-y-2">
                      <h5 className="text-xs font-bold uppercase text-astro-secondary-text tracking-wider">
                        {isHi ? 'ब्रह्मस्थान के अनिवार्य नियम:' : 'Essential Brahmasthan Rules:'}
                      </h5>
                      <ul className="space-y-1.5">
                        {(isHi ? BRAHMASTHAN_DATA.rulesHi : BRAHMASTHAN_DATA.rules).map((rule, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-[#334155]">
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                            <span>{rule}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-astro-secondary-surface border border-[#E2DED5] text-xs text-astro-secondary">
                    <strong className="block font-semibold mb-1">
                      {isHi ? 'दोष निवारण उपाय:' : 'Brahmasthan Correction Remedy:'}
                    </strong>
                    <span>{isHi ? BRAHMASTHAN_DATA.remediesHi : BRAHMASTHAN_DATA.remedies}</span>
                  </div>
                </div>
              ) : (
                /* 16-Zone Details Card */
                <div className="bg-astro-bg rounded-2xl p-6 border border-astro-border shadow-sm space-y-4 h-full flex flex-col justify-between">
                  <div>
                    {/* Direction Header */}
                    <div className="flex items-center justify-between pb-3 border-b border-astro-border">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-astro-primary text-astro-bg">
                            {selectedZone.id}
                          </span>
                          <span className="text-xs text-astro-secondary-text font-medium">
                            {selectedZone.angle}° ({selectedZone.range[0]}° - {selectedZone.range[1]}°)
                          </span>
                        </div>
                        <h4 className="text-xl font-bold text-astro-text font-serif mt-1">
                          {isHi ? selectedZone.nameHi : selectedZone.name}
                        </h4>
                      </div>
                      <div className="text-right text-xs">
                        <span className="block font-semibold text-astro-primary">
                          {isHi ? selectedZone.elementHi : `${selectedZone.element} Element`}
                        </span>
                        <span className="text-astro-secondary-text">
                          {isHi ? selectedZone.rulingPlanetHi : selectedZone.rulingPlanet}
                        </span>
                      </div>
                    </div>

                    {/* Zone Significance */}
                    <div className="mt-3 p-3 rounded-xl bg-astro-card border border-astro-border text-xs text-astro-text leading-relaxed">
                      {isHi ? selectedZone.significanceHi : selectedZone.significance}
                    </div>

                    {/* Favorable vs Unfavorable Items */}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200">
                        <h5 className="font-bold text-emerald-800 flex items-center gap-1.5 mb-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          {isHi ? 'सर्वोत्तम उपयोग (Favorable):' : 'Ideal Placements:'}
                        </h5>
                        <ul className="space-y-1 text-emerald-900">
                          {(isHi ? selectedZone.favorableForHi : selectedZone.favorableFor).map((item, idx) => (
                            <li key={idx} className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="p-3 rounded-xl bg-rose-50/70 border border-rose-200">
                        <h5 className="font-bold text-rose-800 flex items-center gap-1.5 mb-2">
                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                          {isHi ? 'वर्जित (Avoid):' : 'Strictly Forbidden:'}
                        </h5>
                        <ul className="space-y-1 text-rose-900">
                          {(isHi ? selectedZone.unfavorableForHi : selectedZone.unfavorableFor).map((item, idx) => (
                            <li key={idx} className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Remedy Box */}
                  <div className="p-3.5 rounded-xl bg-teal-50 border border-teal-200 text-xs text-teal-950">
                    <strong className="block font-semibold mb-1 text-astro-primary">
                      {isHi ? 'वैदिक वास्तु उपाय:' : 'Vedic Vastu Remedy & Enhancer:'}
                    </strong>
                    <span>{isHi ? selectedZone.remediesHi : selectedZone.remedies}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Selection Grid for 16 Zones */}
          <div className="bg-astro-bg rounded-2xl p-5 border border-astro-border shadow-sm">
            <h4 className="text-sm font-bold text-astro-text font-serif mb-3 flex items-center gap-2">
              <Compass className="w-4 h-4 text-astro-primary" />
              {isHi ? 'सभी 16 दिशाओं की त्वरित सूची' : 'Quick Access 16 Vastu Zones Grid'}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2">
              {VASTU_16_ZONES.map((z) => (
                <button
                  key={z.id}
                  onClick={() => setSelectedZoneId(z.id)}
                  className={`p-2.5 rounded-xl border text-center transition-all ${
                    selectedZoneId === z.id
                      ? 'bg-astro-primary text-astro-bg border-astro-primary shadow-sm'
                      : 'bg-astro-card hover:bg-white text-astro-text border-astro-border'
                  }`}
                >
                  <span className="block font-bold text-sm">{z.id}</span>
                  <span className="block text-[10px] truncate opacity-80">{isHi ? z.nameHi.split(' ')[0] : z.name.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: SIGNIFICANT PLACEMENTS (POOJA ROOM, TIJORI, KITCHEN, ETC.) */}
      {activeSection === 'significant' && (
        <div className="space-y-6">
          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {['All', 'Spiritual', 'Wealth', 'Living', 'Utilities', 'Structure'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    selectedCategory === cat
                      ? 'bg-astro-primary text-white shadow-sm'
                      : 'bg-astro-card text-[#4A5553] hover:bg-astro-border'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="text-xs text-astro-secondary-text">
              {isHi ? 'स्थान का चयन करें और विस्तृत नियम देखें' : 'Click any space to reveal detailed orientation rules'}
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SIGNIFICANT_PLACEMENTS.filter(
              (p) => selectedCategory === 'All' || p.category === selectedCategory
            ).map((placement) => {
              const isSelected = placement.id === selectedPlacementId;
              return (
                <div
                  key={placement.id}
                  onClick={() => setSelectedPlacementId(placement.id)}
                  className={`cursor-pointer rounded-2xl p-5 border transition-all duration-200 ${
                    isSelected
                      ? 'bg-astro-bg border-astro-primary ring-2 ring-astro-primary/20 shadow-md'
                      : 'bg-astro-bg border-astro-border hover:border-astro-primary/50 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-astro-card text-astro-primary border border-astro-border">
                      {placement.category}
                    </span>
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                  </div>
                  <h4 className="text-base font-bold text-astro-text font-serif mb-1.5">
                    {isHi ? placement.titleHi : placement.title}
                  </h4>
                  <p className="text-xs text-astro-secondary-text line-clamp-2 mb-3">
                    {isHi ? placement.orientationGuidelinesHi : placement.orientationGuidelines}
                  </p>
                  <div className="flex items-center justify-between text-xs pt-3 border-t border-astro-border">
                    <span className="font-semibold text-emerald-800">
                      {isHi ? 'सर्वोत्तम: ' : 'Best: '} {placement.bestZones[0].split(' ')[0]}
                    </span>
                    <ChevronRight className="w-4 h-4 text-astro-primary" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected Placement Deep Dive Card */}
          {selectedPlacement && (
            <div className="bg-astro-bg rounded-2xl p-6 sm:p-8 border border-astro-border shadow-sm space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-astro-border">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-astro-primary text-white">
                      {selectedPlacement.category}
                    </span>
                    <span className="text-xs text-astro-secondary-text">
                      {isHi ? 'स्वामी / देवता:' : 'Presiding Deity / Graha:'} {selectedPlacement.deityOrLord}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-astro-text font-serif">
                    {isHi ? selectedPlacement.titleHi : selectedPlacement.title}
                  </h3>
                </div>

                {/* Quick Best vs Forbidden Pills */}
                <div className="flex flex-wrap gap-2">
                  <div className="px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-xs">
                    <strong className="text-emerald-800 block">{isHi ? 'सर्वोत्तम दिशाएं:' : 'Ideal Zones:'}</strong>
                    <span className="text-emerald-950 font-medium">
                      {selectedPlacement.bestZones.map((z) => z.split(' ')[0]).join(', ')}
                    </span>
                  </div>
                  <div className="px-3 py-1.5 rounded-xl bg-rose-50 border border-rose-200 text-xs">
                    <strong className="text-rose-800 block">{isHi ? 'वर्जित दिशाएं:' : 'Forbidden Zones:'}</strong>
                    <span className="text-rose-950 font-medium">
                      {selectedPlacement.forbiddenZones.slice(0, 2).map((z) => z.split(' ')[0]).join(', ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Orientation & Facing Guidelines */}
              <div className="p-4 rounded-xl bg-astro-secondary-surface/70 border border-[#E2DED5]">
                <h4 className="text-xs font-bold uppercase tracking-wider text-astro-secondary mb-1 flex items-center gap-1.5">
                  <Compass className="w-4 h-4 text-[#52635F]" />
                  {isHi ? 'मुख एवं बैठने की दिशा निर्देश:' : 'Facing & Orientation Principles:'}
                </h4>
                <p className="text-xs sm:text-sm text-[#263633] font-medium leading-relaxed">
                  {isHi ? selectedPlacement.orientationGuidelinesHi : selectedPlacement.orientationGuidelines}
                </p>
              </div>

              {/* Core Vastu Rules */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-astro-text font-serif">
                  {isHi ? 'प्रामाणिक वास्तु नियम एवं सावधानियां:' : 'Vedic Rules & Placement Mandates:'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(isHi ? selectedPlacement.vastuRulesHi : selectedPlacement.vastuRules).map((rule, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl bg-astro-card border border-astro-border text-xs text-[#334155] flex items-start gap-2.5"
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{rule}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dosha Effects & Non-destructive Remedies */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-rose-50/80 border border-rose-200">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-rose-900 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    {isHi ? 'गलत दिशा के दुष्परिणाम (Dosha):' : 'Consequences of Faulty Direction:'}
                  </h4>
                  <p className="text-xs text-rose-950 leading-relaxed">
                    {isHi ? selectedPlacement.doshaEffectsHi : selectedPlacement.doshaEffects}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-teal-50/80 border border-teal-200">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-teal-900 mb-2 flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-astro-primary" />
                    {isHi ? 'बिना तोड़-फोड़ के वैदिक उपाय:' : 'Remedies Without Demolition:'}
                  </h4>
                  <ul className="space-y-1.5 text-xs text-teal-950">
                    {(isHi ? selectedPlacement.remediesWithoutDemolitionHi : selectedPlacement.remediesWithoutDemolition).map((rem, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-astro-primary shrink-0 mt-1.5" />
                        <span>{rem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECTION 3: INTERACTIVE ROOM DIRECTION EVALUATOR (QUICK CHECKER) */}
      {activeSection === 'checker' && (
        <div className="bg-astro-bg rounded-2xl p-6 sm:p-8 border border-astro-border shadow-sm space-y-6">
          <div className="max-w-xl">
            <h3 className="text-xl sm:text-2xl font-bold text-astro-text font-serif">
              {isHi ? 'गृह दिशा परीक्षक (Instant Room Evaluator)' : 'Instant Room Direction Evaluator'}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-astro-secondary-text">
              {isHi
                ? 'अपने घर का कोई भी कमरा या वस्तु चुनें और उसकी वर्तमान दिशा का चयन करके तुरंत वास्तु फल व उपाय जानें।'
                : 'Select any room or object and choose its current direction to receive instant Vedic scoring and corrections.'}
            </p>
          </div>

          {/* Interactive Selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 rounded-2xl bg-astro-card border border-astro-border">
            <div>
              <label className="block text-xs font-bold uppercase text-astro-secondary-text mb-2">
                {isHi ? '1. कमरा या वस्तु का प्रकार चुनें:' : '1. Select Room or Significant Type:'}
              </label>
              <select
                value={checkerRoom}
                onChange={(e) => setCheckerRoom(e.target.value)}
                className="w-full bg-white border border-astro-border rounded-xl px-4 py-3 text-sm font-semibold text-astro-text focus:ring-2 focus:ring-astro-primary focus:outline-none"
              >
                {SIGNIFICANT_PLACEMENTS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {isHi ? p.titleHi : p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-astro-secondary-text mb-2">
                {isHi ? '2. घर में उसकी वर्तमान दिशा चुनें:' : '2. Select Current Direction / Zone:'}
              </label>
              <select
                value={checkerZone}
                onChange={(e) => setCheckerZone(e.target.value)}
                className="w-full bg-white border border-astro-border rounded-xl px-4 py-3 text-sm font-semibold text-astro-text focus:ring-2 focus:ring-astro-primary focus:outline-none"
              >
                {VASTU_16_ZONES.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.id} - {isHi ? z.nameHi : z.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Evaluation Result Card */}
          {evalResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-6 rounded-2xl border ${
                evalResult.status === 'excellent'
                  ? 'bg-emerald-50/90 border-emerald-300'
                  : evalResult.status === 'dosh'
                  ? 'bg-rose-50/90 border-rose-300'
                  : 'bg-astro-secondary-surface/90 border-[#E2DED5]'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-black/10">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold ${
                      evalResult.status === 'excellent'
                        ? 'bg-emerald-600 text-white'
                        : evalResult.status === 'dosh'
                        ? 'bg-rose-600 text-white'
                        : 'bg-astro-secondary text-white'
                    }`}
                  >
                    {evalResult.status === 'excellent' ? (
                      <CheckCircle2 className="w-6 h-6" />
                    ) : evalResult.status === 'dosh' ? (
                      <AlertTriangle className="w-6 h-6" />
                    ) : (
                      <Info className="w-6 h-6" />
                    )}
                  </div>
                  <div>
                    <span className="text-xs uppercase font-bold tracking-wider opacity-75">
                      {isHi ? 'वास्तु मूल्यांकन परिणाम' : 'Vastu Evaluation Result'}
                    </span>
                    <h4 className="text-xl font-bold font-serif text-astro-text">
                      {isHi ? evalResult.placement.titleHi : evalResult.placement.title} in{' '}
                      {evalResult.zone?.id} ({isHi ? evalResult.zone?.nameHi : evalResult.zone?.name})
                    </h4>
                  </div>
                </div>

                <div className="text-right">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                      evalResult.status === 'excellent'
                        ? 'bg-emerald-200 text-emerald-900'
                        : evalResult.status === 'dosh'
                        ? 'bg-rose-200 text-rose-900'
                        : 'bg-astro-secondary/20 text-astro-secondary'
                    }`}
                  >
                    {evalResult.status === 'excellent'
                      ? isHi ? 'अति शुभ (Highly Auspicious)' : 'Highly Auspicious'
                      : evalResult.status === 'dosh'
                      ? isHi ? 'वास्तु दोष (Severe Dosh)' : 'Vastu Dosh Detected'
                      : isHi ? 'मध्यम / सामान्य (Neutral)' : 'Moderate / Acceptable'}
                  </span>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-xs">
                <div className="space-y-2">
                  <strong className="block text-sm font-semibold text-astro-text">
                    {isHi ? 'प्रभाव एवं विश्लेषण:' : 'Impact & Analysis:'}
                  </strong>
                  <p className="text-[#334155] leading-relaxed">
                    {evalResult.status === 'excellent'
                      ? isHi
                        ? 'यह स्थान वैदिक वास्तु के अनुसार सर्वोत्तम है। यह परिवार में समृद्धि, मानसिक शांति और सकारात्मक ऊर्जा का संचार करता है।'
                        : 'This is the ideal, highest-yielding zone according to classical Vastu, fostering peace, health, and continuous prosperity.'
                      : evalResult.status === 'dosh'
                      ? isHi ? evalResult.placement.doshaEffectsHi : evalResult.placement.doshaEffects
                      : isHi
                      ? 'यह स्थान मध्यम है। ऊर्जा को और अधिक संतुलित करने के लिए नीचे दिए गए वैदिक उपायों का पालन करें।'
                      : 'This placement is acceptable. Enhancers and subtle directional remedies can elevate its energetic resonance.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <strong className="block text-sm font-semibold text-astro-text">
                    {isHi ? 'अनुशंसित उपाय (Remedy):' : 'Recommended Correction:'}
                  </strong>
                  <ul className="space-y-1 text-[#334155]">
                    {(isHi
                      ? evalResult.placement.remediesWithoutDemolitionHi
                      : evalResult.placement.remediesWithoutDemolition
                    ).map((rem, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-astro-primary shrink-0 mt-1.5" />
                        <span>{rem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* SECTION 4: ASTRO-VASTU BIRTH CHART SYNTHESIS */}
      {activeSection === 'astrovastu' && (
        <div className="bg-astro-bg rounded-2xl p-6 sm:p-8 border border-astro-border shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-astro-primary text-xs font-semibold border border-emerald-200 mb-2">
                <Sun className="w-3.5 h-3.5 text-amber-500" />
                <span>{isHi ? 'जन्म कुंडली एवं गृह वास्तु समन्वय' : 'Kundali Graha & Vastu Synergy'}</span>
              </div>
              <h3 className="text-2xl font-bold text-astro-text font-serif">
                {isHi ? 'एस्ट्रो-वास्तु विश्लेषण (Astro-Vastu)' : 'Personalized Astro-Vastu Analysis'}
              </h3>
              <p className="text-xs sm:text-sm text-astro-secondary-text mt-1">
                {kundali
                  ? isHi
                    ? `सक्रिय कुंडली: ${kundali.birthDetails.name} (${kundali.houses[0]?.sign || 'Aries'} लग्न) के अनुसार दिशाओं की ऊर्जा का विश्लेषण।`
                    : `Linked to ${kundali.birthDetails.name} (${kundali.houses[0]?.sign || 'Aries'} Lagna) birth chart dynamics.`
                  : isHi
                  ? 'कुंडली के 12 भावों और 9 ग्रहों का आपके घर की दिशाओं से प्रत्यक्ष संबंध।'
                  : 'Synthesizing the 12 Bhavas and 9 Grahas with residential directional axes.'}
              </p>
            </div>

            {kundali && (
              <div className="px-4 py-2 rounded-xl bg-astro-card border border-astro-border text-xs">
                <span className="text-astro-secondary-text block">{isHi ? 'लग्न राशि:' : 'Ascendant:'}</span>
                <strong className="text-astro-primary text-sm font-serif">
                  {kundali.houses[0]?.sign || 'Aries'}
                </strong>
              </div>
            )}
          </div>

          {/* Astro-Vastu Correlations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {astroVastuData.map((item, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-astro-card border border-astro-border flex flex-col justify-between space-y-3"
              >
                <div>
                  <div className="flex items-center justify-between pb-2 border-b border-astro-border">
                    <span className="text-xs font-bold text-astro-primary">
                      {isHi ? item.directionHi : item.direction}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                        item.status === 'excellent'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-astro-secondary/15 text-astro-secondary'
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-astro-text font-serif mt-2">
                    {isHi ? item.houseNameHi : item.houseName}
                  </h4>
                  <p className="text-xs text-astro-secondary-text mt-1">
                    <strong className="text-[#334155]">{isHi ? 'ग्रह:' : 'Planet:'}</strong>{' '}
                    {isHi ? item.rulingPlanetHi : item.rulingPlanet}
                  </p>
                  <p className="text-xs text-[#4A5553] mt-2 leading-relaxed">
                    {isHi ? item.significanceHi : item.significance}
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-white border border-astro-border text-xs text-astro-primary">
                  <strong className="block font-semibold mb-0.5">
                    {isHi ? 'वास्तु सलाह:' : 'Astro-Vastu Advice:'}
                  </strong>
                  <span>{item.vastuAdvice}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 5: VEDIC REMEDIES CATALOG (WITHOUT DEMOLITION) */}
      {activeSection === 'remedies' && (
        <div className="bg-astro-bg rounded-2xl p-6 sm:p-8 border border-astro-border shadow-sm space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-astro-secondary-surface text-astro-secondary text-xs font-semibold border border-[#E2DED5] mb-2">
              <Shield className="w-3.5 h-3.5 text-amber-600" />
              <span>{isHi ? 'बिना तोड़-फोड़ के प्रामाणिक वास्तु उपाय' : 'Zero-Demolition Vedic Corrections'}</span>
            </div>
            <h3 className="text-2xl font-bold text-astro-text font-serif">
              {isHi ? 'वास्तु दोष निवारण निर्देशिका' : 'Vastu Dosh Nivaran Directory'}
            </h3>
            <p className="text-xs sm:text-sm text-astro-secondary-text mt-1">
              {isHi
                ? 'धातु की पट्टियां, रंग थेरेपी, पिरामिड, सिद्ध यंत्र और पवित्र वनस्पतियों से घर के दोषों का संपूर्ण निवारण।'
                : 'Rectify structural defects using elemental metal strips, color balancing, Sri Yantras, Pyramids, and Sacred Flora.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Remedy 1: Elemental Metal Strips */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-astro-secondary/15 text-astro-secondary flex items-center justify-center font-bold">
                <Layers className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'धातु की पट्टियां (Metal Strips for Toilets)' : 'Elemental Metal Strips for Toilets'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'गलत दिशा में स्थित शौचालय के चारों ओर 3-इंच चौड़ी धातु की पट्टी (ब्रास, कॉपर या एल्युमिनियम) फर्श पर लगाकर नकारात्मक ऊर्जा को तुरंत काटा जा सकता है।'
                  : 'Embed a 3-inch Brass (SW), Copper (SE/S), or Aluminium (NE/N) strip around the toilet commode to magnetically isolate and block negative drain.'}
              </p>
            </div>

            {/* Remedy 2: Sacred Yantras */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-teal-100 text-astro-primary flex items-center justify-center font-bold">
                <Sparkles className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'सिद्ध यंत्र (Sri & Kubera Yantras)' : 'Energized Sacred Yantras'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'ईशान कोण में श्रीयंत्र और उत्तर दिशा या तिजोरी के अंदर कुबेर यंत्र की स्थापना करने से धन का प्रवाह निरंतर बना रहता है।'
                  : 'Install an energized Sri Yantra in North-East and Kubera Yantra inside the Tijori or North wall to continuously attract liquid wealth.'}
              </p>
            </div>

            {/* Remedy 3: Sacred Plants */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
                <Sun className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'पवित्र वनस्पतियां (Tulsi & Green Flora)' : 'Sacred Vedic Plants'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'ईशान या पूर्व में तुलसी का पौधा और उत्तर में नीली बोतल में मनी प्लांट रखने से वास्तु की प्राण ऊर्जा में तीव्र वृद्धि होती है।'
                  : 'Place holy Tulsi in North-East/East and a Green Money Plant in a blue vase in the North zone to amplify Prana life-force.'}
              </p>
            </div>

            {/* Remedy 4: Himalayan Sea Salt */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-800 flex items-center justify-center font-bold">
                <Droplets className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'समुद्री नमक (Sea Salt Negative Ion Cleanser)' : 'Himalayan Sea Salt Bowls'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'शौचालय या घर के कोनों में एक कटोरी में खड़ा समुद्री नमक रखें और सप्ताह में एक बार बदलें। यह विषाक्त ऊर्जा को सोख लेता है।'
                  : 'Place open glass bowls of raw sea salt in toilets and dark corners to absorb damp sha-chi. Replace every Tuesday/Saturday.'}
              </p>
            </div>

            {/* Remedy 5: Camphor Lamp Ritual */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-800 flex items-center justify-center font-bold">
                <Flame className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'कपूर दीपार्चन (Camphor Purifier)' : 'Camphor Essential Burner'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'प्रतिदिन सांध्यकाल में भीमसेनी कपूर का धुआं पूरे घर में घुमाने से वास्तु के सूक्ष्म कीटाणु और नकारात्मक कंपन नष्ट होते हैं।'
                  : 'Diffuse pure Bhimseni camphor daily at dusk to disintegrate energetic clutter and neutralize planetary affliction signatures.'}
              </p>
            </div>

            {/* Remedy 6: Copper Sun Emblem */}
            <div className="p-5 rounded-2xl bg-astro-card border border-astro-border space-y-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-800 flex items-center justify-center font-bold">
                <Sun className="w-5 h-5" />
              </div>
              <h4 className="text-base font-bold text-astro-text font-serif">
                {isHi ? 'तांबे का सूर्य (Copper Sun for East Wall)' : 'Radiant Copper Sun Emblem'}
              </h4>
              <p className="text-xs text-[#4A5553] leading-relaxed">
                {isHi
                  ? 'यदि पूर्व दिशा में खिड़की न हो या सामाजिक प्रतिष्ठा में कमी आ रही हो तो पूर्व दीवार पर तांबे का सूर्य यंत्र लगाएं।'
                  : 'Hang a cast copper Sun on the East wall to ignite leadership aura, social fame, and clear political obstacles.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
